import { FC } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { t } from 'i18next';

import { theme } from '@store';

import { CareTeamCardDetails } from '@views/pages/Patient/components/CareTeam/components/CareTeamCardDetails.tsx';
import { CareTeamCardDetailsRow } from '@views/pages/Patient/components/CareTeam/components/CareTeamCardDetailsRow.tsx';
import { Delete } from '@mui/icons-material';

export type CareTeamRoles =
  | 'pcp'
  | 'careNavigator'
  | 'assessingProvider'
  | 'geropsych'
  | 'cardiology'
  | 'palliativeCare'
  | 'emergencyContact'
  | 'primaryCaregiver'
  | 'careManager'
  | 'neurology'
  | 'homeHealthProvider'
  | 'externalPCP'
  | 'socialWorker';

interface CareTeamCardProps {
  role: CareTeamRoles;
  detailsVariant?: 'card' | 'contact' | 'practice';
  name?: string;
  value1?: string;
  value2?: string;
  value3?: string;
  value4?: string;
  location?: string;
  editAction?: React.ReactNode;
  removeAction?: () => void;
  cardChips?: string[];
  border?: boolean;
  disableRemove?: boolean;
  inactive?: boolean;
  disable?: boolean;
}

export const CareTeamCard: FC<CareTeamCardProps> = ({
  role,
  name,
  value1,
  value2,
  value3,
  detailsVariant = 'card',
  editAction,
  removeAction,
  location,
  cardChips,
  border = false,
  disableRemove = false,
  inactive = false,
  disable,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.common.white,
        padding: border ? 2 : 0,
        border: border ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='subtitle2'>
            {t(`patient.careTeam.roles.${role}`)}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              mb: 2,
              fontWeight: 400,
              color: inactive ? 'text.disabled' : 'text.primary',
            }}
          >
            {`${name || '-'} ${inactive ? '(Inactive)' : ''}`}
          </Typography>
        </Box>
        {editAction && editAction}
      </Box>
      {cardChips && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 2 }}>
          {cardChips.map((item, index) => {
            return <Chip key={index} label={item} />;
          })}
        </Box>
      )}
      <CareTeamCardDetails
        variant={detailsVariant}
        value1={value1}
        value2={value2}
        value3={value3}
        disabled={disable}
      />
      {location && (
        <Box sx={{ margin: '8px 0' }}>
          <CareTeamCardDetailsRow
            icon='locationOn'
            text={location}
            textWidth='80%'
          />
        </Box>
      )}
      {removeAction && (
        <Button
          sx={{ height: '30px' }}
          color='error'
          startIcon={
            <Delete
              sx={{ height: '20px' }}
              color={disableRemove ? 'disabled' : 'error'}
            />
          }
          onClick={removeAction}
          disabled={disableRemove}
        >
          {t('patient.careTeam.remove')}
        </Button>
      )}
    </Box>
  );
};
